import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { IconChip } from '@/components/ui/calm';
import { Globe, KeyRound, MessageCircleQuestion, ChevronRight, Laptop, ExternalLink, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { getWebApp, haptic } from '@/lib/telegram';
import { ApiError, api } from '@/lib/api';
import { useOpenSite } from '@/lib/site-link';
import { applyLocale } from '@/i18n';
import { toast } from 'sonner';

// skeleton-ok: экран статичный, /info нужен только для ссылки поддержки по нажатию
export const Route = createFileRoute('/settings')({
  component: Settings,
});

function Settings() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { data: info } = useQuery({ queryKey: ['client-info'], queryFn: () => api.info() });

  useEffect(() => {
    const tg = getWebApp();
    if (!tg?.BackButton) return;
    tg.BackButton.show();
    const onBack = () => navigate({ to: '/profile' });
    tg.BackButton.onClick(onBack);
    return () => tg.BackButton.offClick(onBack);
  }, [navigate]);

  /**
   * Переключить язык. Меняем локально сразу, а на бэк отправляем следом: смена языка
   * должна быть мгновенной, а сеть может и не ответить.
   *
   * Сохранить на бэке обязательно — иначе бот продолжит писать на старом языке:
   * `clients.locale` до этого проставлялся один раз при первом `/start` и больше
   * никогда, и переключателю некуда было сохранять.
   */
  const save = useMutation({
    mutationFn: (locale: string) => api.setLocale(locale),
    onError: () => toast.error(t('Language saved on this device only')),
  });

  const toggleLang = () => {
    const next = i18n.language === 'en' ? 'ru' : 'en';
    applyLocale(next);
    haptic('light');
    save.mutate(next);
  };

  const openSupport = () => {
    const tg = getWebApp();
    const url =
      info?.support_url ||
      (info?.bot_username ? `https://t.me/${info.bot_username}?start=open_support` : null);
    if (!url) {
      toast.error(t('Support not set up'));
      return;
    }
    if (tg?.openTelegramLink && url.startsWith('https://t.me/')) tg.openTelegramLink(url);
    else if (tg?.openLink) tg.openLink(url);
    else window.open(url, '_blank');
  };

  return (
    <div className="p-4 pb-24 space-y-6">
      <h2 className="tl-h text-[32px] font-semibold leading-tight tracking-[-0.035em] mb-6 px-2 text-foreground">{t('Settings')}</h2>

      <Card className="overflow-hidden">
        <CardContent className="p-0 divide-y divide-[var(--t-line)]">
          <button
            type="button"
            className="w-full p-4 flex justify-between items-center bg-transparent hover:bg-[var(--t-fill)] transition-colors text-left"
            onClick={toggleLang}
          >
            <div className="flex gap-4 items-center">
              <IconChip icon={Globe} tone="blue" size={36} radius={10} />
              <div className="font-semibold text-foreground">{t('Language')}</div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-bold text-[var(--t-muted)]">
                {i18n.language === 'en' ? 'English' : 'Русский'}
              </span>
              <ChevronRight className="w-4 h-4 text-[var(--t-muted)]" />
            </div>
          </button>
          <SiteRow />
          <WebAccessRow />
          <button
            type="button"
            onClick={openSupport}
            className="w-full p-4 flex justify-between items-center bg-transparent hover:bg-[var(--t-fill)] transition-colors text-left"
          >
            <div className="flex gap-4 items-center">
              <IconChip icon={MessageCircleQuestion} tone="blue" size={36} radius={10} />
              <div className="font-semibold text-foreground">{t('Support')}</div>
            </div>
            <ChevronRight className="w-4 h-4 text-[var(--t-muted)]" />
          </button>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * «Открыть в браузере» (заявка #142): вход в кабинет на сайте-витрине без пароля.
 * Стоит над привязкой почты: ссылка нужна, пока Telegram работает, а почта на случай,
 * когда он недоступен. Нет витрины у бота, нет и строки.
 */
function SiteRow() {
  const { t } = useTranslation();
  const { data: profile } = useQuery({
    queryKey: ['client-profile'],
    queryFn: () => api.profile(),
  });
  const { open, pending } = useOpenSite();
  if (!profile?.site_login_available) return null;
  return (
    <button
      type="button"
      onClick={open}
      disabled={pending}
      className="w-full p-4 flex justify-between items-center bg-transparent hover:bg-[var(--t-fill)] transition-colors text-left disabled:opacity-70"
    >
      <div className="flex gap-4 items-center">
        <IconChip icon={Laptop} tone="blue" size={36} radius={10} />
        <div>
          <div className="font-semibold text-foreground">{t('Open in browser')}</div>
          <div className="text-[12px] leading-[16px] text-[var(--t-muted)] max-w-[220px]">
            {t('Open in browser hint')}
          </div>
        </div>
      </div>
      {pending ? (
        <Loader2 className="w-4 h-4 text-[var(--t-muted)] animate-spin" />
      ) : (
        <ExternalLink className="w-4 h-4 text-[var(--t-muted)]" />
      )}
    </button>
  );
}

/**
 * Привязка почты, чтобы заходить в кабинет на сайте.
 *
 * Нужна тем, кто пришёл из Telegram: клиент из бота и клиент с почтой это разные
 * записи, и без привязки на сайте человека встретит пустой кабинет. Привязывать
 * надо заранее, пока Telegram работает, — в этом весь смысл.
 */
function WebAccessRow() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data: profile, isPending } = useQuery({
    queryKey: ['client-profile'],
    queryFn: () => api.profile(),
  });

  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<'form' | 'code'>('form');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  // На эту почту уже есть аккаунт на сайте: код объединит его с этим Telegram.
  const [merge, setMerge] = useState(false);

  const linked = profile?.email ?? null;

  const sendCode = useMutation({
    mutationFn: () => api.linkEmailRequest(email.trim(), password),
    onSuccess: (r) => {
      setMerge(Boolean(r.merge));
      setStep('code');
      toast.success(t('We sent a code to {{email}}', { email: email.trim() }));
    },
    onError: (e: Error) => {
      // Почта уже у аккаунта на сайте, и объединить его можно только с паролем от него.
      // Текст сервера по-русски, а клиент может читать по-английски.
      const reason =
        e instanceof ApiError
          ? (e.body as { error?: { context?: { reason?: string } } } | null)?.error?.context
              ?.reason
          : undefined;
      toast.error(
        reason === 'merge_wrong_password'
          ? t('Wrong password for the website account with this email')
          : e.message || t('Could not link email'),
      );
    },
  });

  const confirm = useMutation({
    mutationFn: () => api.linkEmailConfirm(code.trim()),
    onSuccess: async (r) => {
      toast.success(
        r.merged
          ? t('Accounts merged, subscriptions from the website are here now')
          : t('Email linked, you can sign in on the website'),
      );
      setOpen(false);
      setStep('form');
      setPassword('');
      setCode('');
      setMerge(false);
      // После объединения здесь появились подписки и баланс сайта: перечитываем всё.
      await qc.invalidateQueries(r.merged ? undefined : { queryKey: ['client-profile'] });
    },
    onError: (e: Error) => toast.error(e.message || t('Could not link email')),
  });

  const busy = sendCode.isPending || confirm.isPending;

  // Строка только там, где у бота есть витрина: без неё почту привязывать не к чему.
  // В 82820dc она уехала на прод раньше сайта и вела всех клиентов в 404.
  if (!profile?.site_url) return null;

  return (
    <div>
      <button
        type="button"
        disabled={isPending || Boolean(linked)}
        onClick={() => {
          haptic('light');
          setOpen((v) => !v);
        }}
        className="w-full p-4 flex justify-between items-center bg-transparent hover:bg-[var(--t-fill)] transition-colors text-left disabled:hover:bg-transparent"
      >
        <div className="flex gap-4 items-center min-w-0">
          <IconChip icon={KeyRound} tone="blue" size={36} radius={10} />
          <div className="min-w-0">
            <div className="font-semibold text-foreground">{t('Web access')}</div>
            <div className="text-[12px] leading-[16px] text-[var(--t-muted)] max-w-[220px]">
              {t('Sign in on the website with your email, even if Telegram is unavailable')}
            </div>
            {/* Почта строкой под описанием, целиком: справа в 110px от адреса
                оставалось «aleksandra.wi…», а описание сжималось в пять строк. */}
            {!isPending && linked && (
              <div className="mt-1 text-[12px] font-bold text-foreground [overflow-wrap:anywhere]">{linked}</div>
            )}
          </div>
        </div>
        {/* Пока профиль едет, статуса нет вовсе: «Не настроен» у человека с
            привязанной почтой читался бы как сброс настройки. */}
        {isPending || linked ? null : (
          <span className="shrink-0 text-sm font-bold text-[var(--t-muted)]">{t('Not set up')}</span>
        )}
      </button>

      {open && !linked ? (
        <div className="px-4 pb-4 space-y-2">
          {step === 'form' ? (
            <>
              <input
                type="email"
                inputMode="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('Email')}
                className="w-full h-11 rounded-xl bg-[var(--t-fill)] px-3 text-[15px] text-foreground outline-none focus:bg-[var(--t-fill)]"
              />
              <input
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('Password')}
                className="w-full h-11 rounded-xl bg-[var(--t-fill)] px-3 text-[15px] text-foreground outline-none focus:bg-[var(--t-fill)]"
              />
              <div className="text-[12px] text-[var(--t-muted)]">
                {t('At least 8 characters. If this email already has a website account, enter its password')}
              </div>
              <button
                type="button"
                disabled={busy || password.length < 8 || !email.includes('@')}
                onClick={() => sendCode.mutate()}
                className="calm-cta h-11 w-full rounded-[var(--r-btn)] font-semibold"
              >
                {t('Send code')}
              </button>
            </>
          ) : (
            <>
              {merge ? (
                <div className="text-[12px] leading-[16px] text-[var(--t-warn)]">
                  {t('This email already has a website account. After confirming, its subscriptions and balance will move here')}
                </div>
              ) : null}
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder={t('Code from the email')}
                className="w-full h-11 rounded-xl bg-[var(--t-fill)] px-3 text-[15px] text-foreground outline-none focus:bg-[var(--t-fill)]"
              />
              <button
                type="button"
                disabled={busy || code.trim().length < 4}
                onClick={() => confirm.mutate()}
                className="calm-cta h-11 w-full rounded-[var(--r-btn)] font-semibold"
              >
                {t('Confirm')}
              </button>
              <button
                type="button"
                onClick={() => setStep('form')}
                className="w-full h-10 rounded-xl text-[14px] text-[var(--t-muted)]"
              >
                {t('Cancel')}
              </button>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}
